// Ordnerbaum links im Explorer.
//
// Eigene, schlanke Komponente statt Fluent `Tree`: das ist im Repo nirgends im
// Einsatz und würde Fremd-Optik in den kvp-rd-Scope ziehen. Die Einrückung
// folgt dem `flattenFolders`-Muster aus mail/MailContextMenu.tsx.
//
// Vollständig kontrolliert: der Baum besitzt keinen eigenen State. Welche
// Ebenen geladen sind, weiß der Adapter (`childrenOf` liefert `undefined`,
// solange eine Ebene noch nicht geladen ist ⇒ Aufklappen fordert sie an).

import * as React from "react";
import { ChevronDown16Regular, ChevronRight16Regular } from "@fluentui/react-icons";
import { FolderIcon } from "./fileTypeIcons";
import { type ExplorerFolder } from "./explorerModel";

const INDENT_PX = 13;

/** Drop-Ziel beim Verschieben per Drag & Drop (kommt vom FileExplorer). */
export type ExplorerDropZiel = {
  nimmtAn: (path: string) => boolean;
  fallenLassen: (path: string) => void;
};

/**
 * Handler, die einen Knopf zum Drop-Ziel machen. `aktiv` hebt ihn hervor,
 * solange der Zug über ihm steht — derselbe Zustand für Baum und Brotkrumen.
 */
export const useDropZiel = (ziel: ExplorerDropZiel | undefined) => {
  const [ueber, setUeber] = React.useState<string | null>(null);
  const props = (path: string) =>
    ziel
      ? {
          onDragOver: (e: React.DragEvent) => {
            if (!ziel.nimmtAn(path)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (ueber !== path) setUeber(path);
          },
          onDragLeave: () => setUeber((u) => (u === path ? null : u)),
          onDrop: (e: React.DragEvent) => {
            setUeber(null);
            if (!ziel.nimmtAn(path)) return;
            e.preventDefault();
            ziel.fallenLassen(path);
          },
        }
      : {};
  return { ueber, props };
};

export const FolderTree: React.FC<{
  /** Beschriftung der Wurzel (z. B. „Kundenakte"). */
  rootLabel: string;
  /** Aktuell geöffneter Ordner ("" = Wurzel). */
  currentPath: string;
  onNavigate: (path: string) => void;
  /** Kinder einer Ebene; `undefined` = noch nicht geladen. */
  childrenOf: (path: string) => ExplorerFolder[] | undefined;
  expandedPaths: ReadonlySet<string>;
  onToggleExpand: (path: string) => void;
  dropZiel?: ExplorerDropZiel;
  /**
   * Rechtsklick auf einen Knoten (PO 28.09.2026: „links in der
   * Ordnerübersicht Rechtsklick"). `null` = die Wurzel.
   */
  onFolderContextMenu?: (folder: ExplorerFolder | null, e: React.MouseEvent) => void;
  /** Ordner im Baum greifen und in einen anderen ziehen. Fehlt es, ist nichts ziehbar. */
  ziehen?: {
    darf: (folder: ExplorerFolder) => boolean;
    starten: (folder: ExplorerFolder, e: React.DragEvent) => void;
  };
}> = ({
  rootLabel,
  currentPath,
  onNavigate,
  childrenOf,
  expandedPaths,
  onToggleExpand,
  dropZiel,
  onFolderContextMenu,
  ziehen,
}) => {
  const drop = useDropZiel(dropZiel);
  const renderLevel = (path: string, depth: number): React.ReactNode => {
    const children = childrenOf(path);
    if (!children || children.length === 0) return null;
    return children.map((f) => {
      const expanded = expandedPaths.has(f.path);
      const loaded = childrenOf(f.path);
      // Solange eine Ebene nicht geladen ist, wird sie als potenziell
      // aufklappbar gezeigt — sonst müsste man erst klicken, um zu sehen,
      // dass es etwas zu klappen gibt.
      const maybeHasChildren = loaded === undefined || loaded.length > 0;
      return (
        <React.Fragment key={f.path}>
          {/* Auf-/Zuklappen und Navigieren sind zwei getrennte Schaltflächen
              nebeneinander — ein Knopf IM Knopf wäre ungültiges Markup und für
              die Tastaturbedienung eine Sackgasse. */}
          <div className="docx-tree-line" style={{ paddingLeft: depth * INDENT_PX }}>
            <button
              type="button"
              className={"docx-tw" + (maybeHasChildren ? "" : " leaf")}
              aria-label={`${f.name} ${expanded ? "zuklappen" : "aufklappen"}`}
              aria-expanded={maybeHasChildren ? expanded : undefined}
              tabIndex={maybeHasChildren ? 0 : -1}
              onClick={() => onToggleExpand(f.path)}
            >
              {expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            </button>
            <button
              type="button"
              className={
                "docx-tree-row" + (currentPath === f.path ? " on" : "") + (drop.ueber === f.path ? " drop" : "")
              }
              onClick={() => onNavigate(f.path)}
              title={f.name}
              onContextMenu={onFolderContextMenu ? (e) => onFolderContextMenu(f, e) : undefined}
              draggable={ziehen?.darf(f) || undefined}
              onDragStart={ziehen?.darf(f) ? (e) => ziehen.starten(f, e) : undefined}
              {...drop.props(f.path)}
            >
              <FolderIcon size={16} />
              <span className="docx-tree-label">{f.name}</span>
              {f.count != null && f.count > 0 ? (
                <span className="docx-tree-count" title={`${f.count} Dateien inkl. Unterordner`}>
                  {f.count}
                </span>
              ) : null}
            </button>
          </div>
          {expanded ? renderLevel(f.path, depth + 1) : null}
        </React.Fragment>
      );
    });
  };

  const rootChildren = childrenOf("");

  return (
    // Der scrollende `.docx-tree`-Container kommt vom FileExplorer (er hält die
    // per Splitter gezogene Breite) — hier nur der Inhalt.
    <nav aria-label="Ordnerstruktur">
      <div className="docx-tree-line">
        <span className="docx-tw leaf" aria-hidden>
          <ChevronDown16Regular />
        </span>
        <button
          type="button"
          className={"docx-tree-row" + (currentPath === "" ? " on" : "") + (drop.ueber === "" ? " drop" : "")}
          onClick={() => onNavigate("")}
          title={rootLabel}
          onContextMenu={onFolderContextMenu ? (e) => onFolderContextMenu(null, e) : undefined}
          {...drop.props("")}
        >
          <FolderIcon size={16} />
          <span className="docx-tree-label">{rootLabel}</span>
        </button>
      </div>
      {renderLevel("", 1)}
      {rootChildren && rootChildren.length === 0 ? (
        <div className="docx-tree-empty">Keine Ordner</div>
      ) : null}
    </nav>
  );
};
